import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { authApi } from '@/lib/erpApi';
import { Loader2, History, Search } from 'lucide-react';

const MODULES = ['buildings', 'units', 'tenants', 'agreements', 'emi_customers', 'emi_plans'];
const ACTIONS = ['create', 'update', 'delete'];

const actionColor = {
  create: 'bg-emerald-100 text-emerald-800',
  update: 'bg-sky-100 text-sky-800',
  delete: 'bg-rose-100 text-rose-800',
};

/** Read-only audit trail. Every mutation from ERP endpoints is logged here. */
export default function AuditLogsPage() {
  const { token } = useAuth();
  const api = authApi(token);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ module: 'all', action: 'all' });
  const [q, setQ] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/erp/audit-logs', {
      params: {
        limit: 300,
        ...(filter.module !== 'all' && { module: filter.module }),
        ...(filter.action !== 'all' && { action: filter.action }),
      },
    }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter]);

  const filtered = q
    ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()))
    : rows;

  return (
    <div className="space-y-6" data-testid="erp-audit-logs-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-gray-500 text-sm mt-1">Read-only history of every mutation in the ERP.</p>
      </div>

      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search logs" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={filter.module} onValueChange={(v) => setFilter({ ...filter, module: v })}>
          <SelectTrigger><SelectValue placeholder="All modules" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modules</SelectItem>
            {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.action} onValueChange={(v) => setFilter({ ...filter, action: v })}>
          <SelectTrigger><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No audit entries.</CardContent></Card>
      ) : (
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Module</th>
                <th className="p-3">Action</th>
                <th className="p-3">Entity</th>
                <th className="p-3">Actor</th>
                <th className="p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 text-xs font-mono text-gray-600 whitespace-nowrap">
                    <History className="inline h-3.5 w-3.5 mr-1 text-gray-400" />
                    {new Date(r.created_at).toLocaleString('en-IN')}
                  </td>
                  <td className="p-3 text-gray-700">{r.module}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${actionColor[r.action] || 'bg-gray-100'}`}>{r.action}</span></td>
                  <td className="p-3 text-gray-800">{r.entity_label || <span className="font-mono text-xs text-gray-400">{(r.entity_id || '').slice(0, 8)}</span>}</td>
                  <td className="p-3 text-gray-600">{r.actor}</td>
                  <td className="p-3 font-mono text-xs text-gray-500">{r.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}
      <p className="text-xs text-gray-400">Showing {filtered.length} entries.</p>
    </div>
  );
}
